
import { useState, useEffect } from 'react';

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white font-inter">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md border-b border-gray-200' : 'bg-transparent'
      }`}>
        <div className="w-full px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/273542ef-fb02-4d46-9bb3-914ba78a8ccd.png" 
                alt="ERIMATECH LTDA Logo" 
                className="w-8 h-8 object-contain"
              />
              <h1 className={`text-xl font-bold transition-colors ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}>
                ERIMATECH LTDA
              </h1>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => scrollToSection('about')}
                className={`font-medium hover:opacity-75 transition-all cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-gray-700' : 'text-white/90'
                }`}
              >
                About Us
              </button>
              <span className={`w-px h-4 ${isScrolled ? 'bg-gray-300' : 'bg-white/30'}`}></span>
              <button 
                onClick={() => scrollToSection('products')}
                className={`font-medium hover:opacity-75 transition-all cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-gray-700' : 'text-white/90'
                }`}
              >
                Products
              </button>
              <span className={`w-px h-4 ${isScrolled ? 'bg-gray-300' : 'bg-white/30'}`}></span>
              <button 
                onClick={() => scrollToSection('features')}
                className={`font-medium hover:opacity-75 transition-all cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-gray-700' : 'text-white/90'
                }`}
              >
                Features
              </button>
              <span className={`w-px h-4 ${isScrolled ? 'bg-gray-300' : 'bg-white/30'}`}></span>
              <button 
                onClick={() => scrollToSection('contact')}
                className={`font-medium hover:opacity-75 transition-all cursor-pointer whitespace-nowrap ${
                  isScrolled ? 'text-gray-700' : 'text-white/90'
                }`}
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/5075afb13baa92dbf516c54a957f8aff.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 text-center px-6 w-full max-w-6xl mx-auto">
          <div className="space-y-6 mb-16">
            <h2 className="text-5xl md:text-7xl font-black text-white leading-tight">
              Innovative iOS Solutions
            </h2>
            <h3 className="text-4xl md:text-6xl font-black text-white/90 leading-tight">
              for Modern Users
            </h3>
            <p className="text-xl md:text-2xl text-white/80 font-medium">
              VPN & Cleaner Technology
            </p>
            <p className="text-lg text-white/70 max-w-2xl mx-auto leading-relaxed mt-8">
              Expert team developing cutting-edge applications for the iOS platform, 
              enhancing user experience and improving mobile device security
            </p>
          </div>

          {/* Product Mockup Cards */}
          <div className="relative flex justify-center items-end space-x-8 h-80">
            {/* VPN Card - Center */}
            <div className="transform rotate-2 scale-110 z-30 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl p-6 w-48 h-72 shadow-2xl">
              <div className="bg-black/20 rounded-xl h-full flex flex-col items-center justify-center text-white">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-2xl"></i>
                </div>
                <h4 className="font-bold text-lg mb-2">VPN Protection</h4>
                <p className="text-sm opacity-80 text-center px-2">Secure Connection</p>
              </div>
            </div>

            {/* Cleaner Card - Left */}
            <div className="transform -rotate-3 z-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 w-44 h-64 shadow-xl">
              <div className="bg-black/20 rounded-xl h-full flex flex-col items-center justify-center text-white">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-broom-line text-2xl"></i>
                </div>
                <h4 className="font-bold text-lg mb-2">Smart Cleaner</h4>
                <p className="text-sm opacity-80 text-center px-2">Storage Optimizer</p>
              </div>
            </div>

            {/* Security Card - Right */}
            <div className="transform rotate-3 z-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl p-6 w-44 h-64 shadow-xl">
              <div className="bg-black/20 rounded-xl h-full flex flex-col items-center justify-center text-white">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-lock-line text-2xl"></i>
                </div>
                <h4 className="font-bold text-lg mb-2">Security Suite</h4>
                <p className="text-sm opacity-80 text-center px-2">Privacy Tools</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 h-full">
                <p className="text-sm font-semibold text-emerald-600 uppercase tracking-wider mb-4">ABOUT US</p>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 font-playfair leading-tight">
                  Expert Team Building Tomorrow's Apps
                </h2>
              </div>
            </div>
            
            <div className="lg:col-span-3">
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  <strong>ERIMATECH LTDA</strong> is a team of experts developing modern applications for the iOS platform. 
                  Our products, such as VPN and Cleaner, are designed to enhance user experience and improve 
                  the security of mobile devices.
                </p>
                <p>
                  We specialize in creating innovative solutions that address the evolving needs of iOS users. 
                  Our development approach combines cutting-edge technology with intuitive design principles, 
                  ensuring that every application we create delivers exceptional performance and user satisfaction.
                </p>
                <p>
                  With years of experience in iOS development, our team understands the importance of privacy, 
                  security, and performance optimization. We are committed to building applications that not only 
                  meet current market demands but also anticipate future technological trends.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Products</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Professional iOS applications designed to enhance your mobile experience
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 h-96">
            {/* VPN Product - Large Card */}
            <div className="lg:col-span-7 relative group cursor-pointer">
              <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl h-full p-8 overflow-hidden relative transform transition-transform duration-300 group-hover:scale-105">
                <div 
                  className="absolute inset-0 opacity-20"
                  style={{
                    backgroundImage: `url('https://readdy.ai/api/search-image?query=iPhone%20displaying%20VPN%20application%20interface%20with%20security%20shield%20icons%20floating%20around%20device%20blue%20gradient%20background%20modern%20UI%20design%20connected%20network%20visualization%20cyber%20security%20theme%20professional%20mobile%20app%20design&width=800&height=600&seq=vpn002&orientation=landscape')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                ></div>
                
                <div className="relative z-10 h-full flex flex-col justify-between">
                  <div>
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                      <i className="ri-shield-check-line text-3xl text-white"></i>
                    </div>
                    <h3 className="text-3xl font-bold text-white mb-4">VPN Protection</h3>
                    <ul className="text-white/90 space-y-2">
                      <li className="flex items-center">
                        <i className="ri-check-line text-lg mr-3"></i>
                        Secure internet connection
                      </li>
                      <li className="flex items-center">
                        <i className="ri-check-line text-lg mr-3"></i>
                        Privacy protection
                      </li>
                      <li className="flex items-center">
                        <i className="ri-check-line text-lg mr-3"></i>
                        Global server network
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Two Stacked Cards */}
            <div className="lg:col-span-5 space-y-8">
              {/* Cleaner App Card */}
              <div className="relative group cursor-pointer h-44">
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl h-full p-6 overflow-hidden relative transform transition-transform duration-300 group-hover:scale-105">
                  <div 
                    className="absolute inset-0 opacity-20"
                    style={{
                      backgroundImage: `url('https://readdy.ai/api/search-image?query=iPhone%20showing%20cleaner%20application%20interface%20with%20storage%20optimization%20graphics%20and%20cleaning%20progress%20indicators%20green%20gradient%20background%20modern%20mobile%20app%20UI%20performance%20metrics%20clean%20design&width=600&height=400&seq=clean003&orientation=landscape')`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  ></div>
                  
                  <div className="relative z-10 h-full flex items-center justify-between">
                    <div>
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-3">
                        <i className="ri-broom-line text-xl text-white"></i>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Smart Cleaner</h3>
                      <p className="text-white/90 text-sm">Storage optimization</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance Card */}
              <div className="relative group cursor-pointer h-44">
                <div className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl h-full p-6 overflow-hidden relative transform transition-transform duration-300 group-hover:scale-105">
                  <div 
                    className="absolute inset-0 opacity-20"
                    style={{
                      backgroundImage: `url('https://readdy.ai/api/search-image?query=iPhone%20displaying%20performance%20monitoring%20dashboard%20with%20speed%20boost%20metrics%20and%20system%20optimization%20charts%20teal%20gradient%20background%20modern%20iOS%20app%20interface%20performance%20analytics&width=600&height=400&seq=perf004&orientation=landscape')`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  ></div>
                  
                  <div className="relative z-10 h-full flex items-center justify-between">
                    <div>
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-3">
                        <i className="ri-flashlight-line text-xl text-white"></i>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Performance Boost</h3>
                      <p className="text-white/90 text-sm">Speed optimization</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Built with Cutting-Edge Technology
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our applications leverage the latest iOS technologies to deliver superior performance and user experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Security Feature */}
            <div className="text-center group hover:bg-white hover:shadow-lg transition-all duration-300 rounded-2xl p-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-transform duration-300">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Advanced Security</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• End-to-end encryption</li>
                <li>• Zero-log policy</li>
                <li>• Secure protocols</li>
              </ul>
            </div>

            {/* Speed Feature */}
            <div className="text-center group hover:bg-white hover:shadow-lg transition-all duration-300 rounded-2xl p-6">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-transform duration-300">
                <i className="ri-flashlight-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Lightning Speed</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Optimized performance</li>
                <li>• Fast processing</li>
                <li>• Minimal battery usage</li>
              </ul>
            </div>

            {/* Connectivity Feature */}
            <div className="text-center group hover:bg-white hover:shadow-lg transition-all duration-300 rounded-2xl p-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-transform duration-300">
                <i className="ri-global-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Global Connectivity</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Worldwide servers</li>
                <li>• Reliable connections</li>
                <li>• 24/7 availability</li>
              </ul>
            </div>

            {/* Privacy Feature */}
            <div className="text-center group hover:bg-white hover:shadow-lg transition-all duration-300 rounded-2xl p-6">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-transform duration-300">
                <i className="ri-lock-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Privacy Protection</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Anonymous browsing</li>
                <li>• Data protection</li>
                <li>• Identity security</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <footer id="contact" className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start mb-12">
            <div className="mb-6 md:mb-0">
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/273542ef-fb02-4d46-9bb3-914ba78a8ccd.png" 
                  alt="ERIMATECH LTDA Logo" 
                  className="w-10 h-10 object-contain"
                />
                <h2 className="text-2xl font-bold">ERIMATECH LTDA</h2>
              </div>
              <p className="text-gray-400">iOS Development Experts</p>
            </div>
            
            <div className="text-right">
              <p className="text-gray-400 max-w-md">
                Building the future of iOS applications with innovative solutions and cutting-edge technology
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Products Column */}
            <div>
              <h3 className="font-bold text-lg mb-6 text-white">PRODUCTS</h3>
              <ul className="space-y-3 text-gray-400">
                <li>VPN Protection</li>
                <li>Smart Cleaner</li>
                <li>Security Suite</li>
                <li>Performance Tools</li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h3 className="font-bold text-lg mb-6 text-white">COMPANY</h3>
              <ul className="space-y-3 text-gray-400">
                <li>About Us</li>
                <li>Technology</li>
                <li>Our Team</li>
                <li>Careers</li>
              </ul>
            </div>

            {/* Resources Column */}
            <div>
              <h3 className="font-bold text-lg mb-6 text-white">RESOURCES</h3>
              <ul className="space-y-3 text-gray-400">
                <li>Documentation</li>
                <li>Support</li>
                <li>Privacy Policy</li>
                <li>Terms of Service</li>
              </ul>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="font-bold text-lg mb-6 text-white">CONTACT INFORMATION</h3>
              <div className="space-y-4 text-gray-400">
                <div className="flex items-start">
                  <i className="ri-map-pin-line text-lg mr-3 mt-1 text-gray-500"></i>
                  <div>
                    <p><strong>Address:</strong> Rua GUAIRA 41 SALA C3</p>
                    <p><strong>City:</strong> SAO FRANCISCO DO SUL</p>
                    <p><strong>Zip:</strong> 89240-000</p>
                    <p><strong>Country:</strong> Brazil</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <i className="ri-mail-line text-lg mr-3 text-gray-500"></i>
                  <div>
                    <p><strong>Email:</strong> help@erimatechltda.com</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <i className="ri-phone-line text-lg mr-3 text-gray-500"></i>
                  <div>
                    <p><strong>Number:</strong> +1 5706724504</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="border-t border-gray-800 pt-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
              {/* Office Image */}
              <div className="order-2 lg:order-1">
                <div 
                  className="w-full h-32 rounded-2xl bg-gradient-to-br from-blue-900 to-purple-900"
                  style={{
                    backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20office%20workspace%20with%20iOS%20devices%20iPhone%20iPad%20on%20sleek%20desk%20professional%20atmosphere%20soft%20lighting%20tech%20company%20environment%20clean%20minimalist%20design%20contemporary%20office%20space&width=400&height=200&seq=office005&orientation=landscape')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                ></div>
              </div>

              {/* Tagline */}
              <div className="text-center order-1 lg:order-2">
                <h4 className="text-2xl md:text-3xl font-playfair text-gray-300 leading-tight">
                  Building the Future<br/>
                  of iOS<br/>
                  Applications
                </h4>
              </div>

              {/* Copyright */}
              <div className="text-right order-3">
                <p className="text-xl font-playfair text-gray-300 mb-2">© ERIMATECH LTDA 2025</p>
                <p className="text-gray-500 text-sm">All rights reserved</p>
                <a 
                  href="https://readdy.ai/?ref=logo" 
                  target="_blank" 
                  rel="nofollow noopener noreferrer" 
                  className="inline-block mt-2 text-gray-500 hover:text-gray-400 transition-colors text-sm cursor-pointer"
                >
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
